/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { InsertAsientoContableCommandProps } from "api/Commands/InsertAsientoContableCommand"
import { LineaImpuestoInsertAsientoContableCommandProps } from "api/Commands/LineaImpuestoInsertAsientoContableCommand"
import { LineaImpuestoUpdateAsientoContableCommandProps } from "api/Commands/LineaImpuestoUpdateAsientoContableCommand"
import { LineaInsertAsientoContableCommandProps } from "api/Commands/LineaInsertAsientoContableCommand"
import { LineaUpdateAsientoContableCommandProps } from "api/Commands/LineaUpdateAsientoContableCommand"
import { UpdateAsientoContableCommandProps } from "api/Commands/UpdateAsientoContableCommand"
import { FicheroGetAsientoContableByIdDtoProps } from "api/DTOs/FicheroGetAsientoContableByIdDto"
import {
  IMPUESTO_CATEGORIA,
  ImpuestoCategoriaType,
} from "api/Types/ImpuestoCategoria"
import { TIPO_ASIENTO, TipoAsientoType } from "api/Types/TipoAsiento"
import { TIPO_LINEA_ASIENTO } from "api/Types/TipoLineaAsiento"
import { TFunction } from "i18next"
import { backendDateFormat } from "utils/formatDate"
import { precisionNumber } from "utils/precisionNumber"
import { v4 as uuidv4 } from "uuid"
import * as Yup from "yup"

export interface LineaAsientoFormikProps {
  id: string
  cuentaContableId: string | undefined
  numeroDocumento: string
  descripcion: string
  listadoTags: string[]
  debe: number | null
  haber: number | null
}

export interface LineaImpuestoFormikProps {
  id: string
  categoria: ImpuestoCategoriaType
  claveImpuesto: string | null | undefined
  porcentajeImpuesto: number | null
  cuentaContableId: string | undefined
  subtotal: number | null
  valorImpuesto: number
}

export interface AsientoContableFormikProps {
  isEdit: boolean
  id: string | undefined
  fecha: Date
  ficheros: FicheroGetAsientoContableByIdDtoProps[]
  lineas: LineaAsientoFormikProps[]
  lineasImpuesto: LineaImpuestoFormikProps[]
  notas: string | null
  numero: number | string | null | undefined
  tipoAsiento: TipoAsientoType
  tipoImportacionAsiento: number | null | undefined
  descuadre: number
}

export const adapterPutAsientoContable = (
  valuesFormik: AsientoContableFormikProps
): UpdateAsientoContableCommandProps => ({
  id: valuesFormik.id ?? "",
  fecha: backendDateFormat(valuesFormik.fecha),
  lineas:
    valuesFormik.lineas
      ?.filter(
        (linea: LineaAsientoFormikProps) =>
          linea.cuentaContableId !== undefined &&
          (linea.debe !== null || linea.haber !== null)
      )
      ?.map(
        (linea): LineaUpdateAsientoContableCommandProps => ({
          id: linea.id,
          cargoAbono:
            linea.debe !== null
              ? TIPO_LINEA_ASIENTO.DEBE
              : TIPO_LINEA_ASIENTO.HABER,
          cuentaContableId: linea.cuentaContableId ?? "",
          descripcion: linea.descripcion,
          importe: precisionNumber(linea?.debe ?? linea?.haber ?? 0),
          numeroDocumento: linea.numeroDocumento,
          tags: linea.listadoTags ?? [],
        })
      ) ?? null,
  lineasImpuesto:
    valuesFormik.lineasImpuesto
      ?.filter(
        (linea: LineaImpuestoFormikProps) =>
          linea.claveImpuesto !== undefined &&
          linea.cuentaContableId !== undefined &&
          linea.subtotal !== undefined
      )
      ?.map(
        (
          lineaImpuesto: LineaImpuestoFormikProps
        ): LineaImpuestoUpdateAsientoContableCommandProps => ({
          categoria: lineaImpuesto.categoria,
          claveImpuesto: lineaImpuesto.claveImpuesto ?? "",
          cuentaContableId: lineaImpuesto.cuentaContableId ?? "",
          porcentajeImpuesto: lineaImpuesto.porcentajeImpuesto ?? 0,
          subtotal: precisionNumber(lineaImpuesto.subtotal ?? 0),
          valorImpuesto: precisionNumber(lineaImpuesto.valorImpuesto),
        })
      ) ?? null,
  notas: valuesFormik.notas,
  tipoAsiento: valuesFormik.tipoAsiento,
})

export const adapterPostAsientoContable = (
  valuesFormik: AsientoContableFormikProps
): InsertAsientoContableCommandProps => ({
  fecha: backendDateFormat(valuesFormik.fecha),
  ficheros: [],
  lineas:
    valuesFormik.lineas
      ?.filter(
        (linea: LineaAsientoFormikProps) =>
          linea.cuentaContableId !== undefined &&
          (linea.debe !== null || linea.haber !== null)
      )
      .map(
        (
          linea: LineaAsientoFormikProps
        ): LineaInsertAsientoContableCommandProps => ({
          cargoAbono:
            linea.debe !== null
              ? TIPO_LINEA_ASIENTO.DEBE
              : TIPO_LINEA_ASIENTO.HABER,
          cuentaContableId: linea.cuentaContableId ?? "",
          descripcion: linea.descripcion,
          importe: precisionNumber(linea?.debe ?? linea?.haber ?? 0),
          numeroDocumento: linea.numeroDocumento,
        })
      ) ?? null,
  lineasImpuesto:
    valuesFormik.lineasImpuesto
      ?.filter(
        (linea: LineaImpuestoFormikProps) =>
          linea.claveImpuesto !== undefined &&
          linea.cuentaContableId !== undefined &&
          linea.subtotal !== undefined
      )
      .map(
        (
          lineaImpuesto: LineaImpuestoFormikProps
        ): LineaImpuestoInsertAsientoContableCommandProps => ({
          categoria: lineaImpuesto.categoria,
          claveImpuesto: lineaImpuesto.claveImpuesto ?? "",
          cuentaContableId: lineaImpuesto.cuentaContableId ?? "",
          porcentajeImpuesto: lineaImpuesto.porcentajeImpuesto ?? 0,
          subtotal: precisionNumber(lineaImpuesto.subtotal ?? 0),
          valorImpuesto: precisionNumber(lineaImpuesto.valorImpuesto),
        })
      ) ?? null,
  notas: valuesFormik.notas,
  tipoAsiento: valuesFormik.tipoAsiento,
})

export const createLineaDefault = () => ({
  id: uuidv4(),
  cuentaContableId: undefined,
  numeroDocumento: "",
  descripcion: "",
  listadoTags: [],
  debe: null,
  haber: null,
})

export const LINEAS_DEFAULT = [
  createLineaDefault(),
  createLineaDefault(),
  createLineaDefault(),
]

export const INITIAL_VALUES_DEFAULT = {
  isEdit: false,
  id: undefined,
  fecha: new Date(),
  lineas: LINEAS_DEFAULT,
  lineasImpuesto: [
    {
      id: uuidv4(),
      categoria: IMPUESTO_CATEGORIA.IVA,
      porcentajeImpuesto: null,
      cuentaContableId: undefined,
      claveImpuesto: undefined,
      subtotal: null,
      valorImpuesto: 0,
    },
  ],
  ficheros: [],
  notas: "",
  numero: undefined,
  tipoAsiento: TIPO_ASIENTO.APUNTE,
  tipoImportacionAsiento: undefined,
  descuadre: 0,
}

export const validationSchemaAsientosFormik = (t: TFunction) =>
  Yup.object().shape({
    lineas: Yup.array().of(
      Yup.object().shape({
        cuentaContableId: Yup.string().when(["debe", "haber"], {
          is: (debe: number | null, haber: number | null) => {
            return debe ?? haber
          },
          then: Yup.string().required(
            `${t("cuadroCuentas.msg.errorAccountLine")}`
          ),
          otherwise: Yup.string().nullable(),
        }),
      })
    ),

    lineasImpuesto: Yup.array().of(
      Yup.object().shape({
        cuentaContableId: Yup.string()
          .nullable()
          .test("compareVariables", "Error", (value, { parent }) => {
            const { subtotal, porcentajeImpuesto } = parent
            if (value !== undefined) {
              return true
            }

            if (subtotal !== null || porcentajeImpuesto !== null) {
              return false
            }
            return true
          }),
        porcentajeImpuesto: Yup.number()
          .nullable()
          .transform((value, originalValue) => {
            return Number.isNaN(value) ? originalValue : value
          })
          .test(
            "compareVariables",
            "Error: porcentajeImpuesto debe ser definido si subtotal tiene valor",
            (value, { parent }) => {
              const { subtotal, cuentaContableId } = parent
              if (value !== null) {
                return true
              }

              if (subtotal !== null) {
                return false
              }
              if (cuentaContableId !== undefined) {
                return false
              }
              return true
            }
          ),
        subtotal: Yup.number()
          .nullable()
          .test(
            "compareVariables",
            "Error: subtotal debe ser definido si porcentajeImpuesto tiene valor",
            (value, { parent }) => {
              const { porcentajeImpuesto, cuentaContableId } = parent
              if (value !== null) {
                return true
              }

              if (porcentajeImpuesto !== null) {
                return false
              }
              if (cuentaContableId !== undefined) {
                return false
              }
              return true
            }
          ),
      })
    ),
  })
